
import { Form, TextField, PasswordField, SelectOption } from "../util";
import { defineComponent } from "vue";
import { mapActions } from "vuex";
import hash from "object-hash";

import { ProfessionalBody, ActingBody } from "@/domain/params";
import {
  actingService,
  clinicService,
  professionalService,
  specialtyService,
} from "@/domain/services";
import { OptionSelect } from "@/presentation/models";

type ComponentData = {
  professional_id?: number;
  acting_id?: number;
  isLoading: boolean;
  field_valid: { [field: string]: boolean };
  body: ProfessionalBody;
  acting_body: ActingBody;
  body_hash?: string;
  acting_body_hash?: string;
  select_options: {
    clinics: OptionSelect[];
    specialties: OptionSelect[];
  };
};

export default defineComponent({
  name: "ProfessionalForm",
  components: { Form, TextField, PasswordField, SelectOption },
  data(): ComponentData {
    const professional_id = Number(this.$route.params.professional_id);
    return {
      professional_id,
      isLoading: false,
      field_valid: {
        name: !!professional_id,
        phone: !!professional_id,
        email: !!professional_id,
        username: !!professional_id,
        password: !!professional_id,
        clinic: !!professional_id,
        specialty: !!professional_id,
      },
      body: {
        name: "",
        phone: "",
        username: "",
        email: "",
        password: "",
      },
      acting_body: {
        professional_id: 0,
        clinic_id: 0,
        specialty_id: 0,
      },
      select_options: {
        clinics: [],
        specialties: [],
      },
    };
  },
  async created() {
    await this.populateSelects();
    if (this.professional_id) await this.loadProfessional(this.professional_id);
  },
  computed: {
    isFormValid() {
      for (let field_name in this.field_valid)
        if (!this.field_valid[field_name]) return false;
      return true;
    },
  },
  methods: {
    loadProfessional(professional_id: number) {
      this.isLoading = true;
      return professionalService
        .getById(professional_id)
        .then((professional) => {
          this.isLoading = false;
          this.body = {
            name: professional.name,
            phone: professional.phone.maskPhone(),
            email: professional.email,
            username: professional.username,
            password: "",
            reg_number: professional.reg_number,
          };
          const acting = professional.actuations?.[0];
          this.acting_id = acting?.id;
          this.acting_body = {
            specialty_id: acting?.specialty_id || 0,
            clinic_id: acting?.clinic_id || 0,
            professional_id: professional.id,
          };

          this.body_hash = hash(this.body);
          this.acting_body_hash = hash(this.acting_body);
        })
        .catch(() => {
          this.$router.push("/professionals");
        });
    },
    async saveActing(professional_id?: number) {
      const payload = { ...this.acting_body };

      if (this.acting_id && this.acting_body_hash != hash(this.acting_body))
        return actingService
          .update(this.acting_id, payload)
          .then(() => {
            this.reloadProfessionals();
            this.$router.push("/professionals");
          })
          .catch(() => {
            this.isLoading = false;
          });
      else if (!this.acting_id && professional_id) {
        payload.professional_id = professional_id;
        return actingService
          .create(payload)
          .then(async () => {
            this.reloadProfessionals();
            this.$router.push("/professionals");
          })
          .catch(() => {
            this.isLoading = false;
          });
      }
      this.$router.push("/professionals");
    },
    save() {
      this.isLoading = true;
      const payload = {
        ...this.body,
        phone: this.body.phone.removeMask(),
      };

      if (!payload.password) delete payload.password;

      if (this.professional_id && this.body_hash != hash(this.body)) {
        return professionalService
          .update(this.professional_id, payload)
          .then(() => {
            this.saveActing();
          })
          .catch(() => {
            this.isLoading = false;
          });
      } else if (!this.professional_id)
        return professionalService
          .create(payload)
          .then((professional) => {
            this.saveActing(professional.id);
          })
          .catch(() => {
            this.isLoading = false;
          });
      this.saveActing();
    },
    closeModal(e: Event) {
      if (
        e.target == this.$refs.modal_mask ||
        e.target == this.$refs.close_modal
      )
        this.$router.push("/professionals");
    },
    updateValidation(field: string, valid: boolean) {
      this.field_valid[field] = valid;
    },
    async populateSelects() {
      await clinicService.load().then((clinics) => {
        this.select_options.clinics = clinics.map((c) => ({
          value: c.id,
          label: c.name,
        }));
      });

      await specialtyService.load().then((specialties) => {
        this.select_options.specialties = specialties.map((s) => ({
          value: s.id,
          label: s.description,
        }));
      });
    },
    ...mapActions({ reloadProfessionals: "professionals/load" }),
  },
});
